import { Box, Divider, IconButton, TextField, Tooltip, Typography } from "@material-ui/core";
import { Fullscreen, Save } from "@material-ui/icons";
import { styled } from "@material-ui/styles";
import { useEffect, useState } from "react";
import axios from "axios";
import { REST_API_END_POINT } from 'src/constants/Defaultvalues'; // adjust if needed
import { useSnackbar } from "notistack";
import moment from "moment"; // Make sure to install moment.js if not already

const StickyNote = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    padding: '1em',
    fontSize: '14px',
    letterSpacing: '1px',
    position: 'relative',
    backgroundColor: '#507db8',
    color: '#fff',
    boxShadow: '0px 4px 10px rgba(0,0,0,0.3)',
    overflowWrap: 'break-word',
    fontFamily: `'Loved by the King', cursive`,
    borderRadius: '8px',
    margin: 'auto',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.05)',
    },
    '&::before': {
        content: '""',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
        height: '40px',
        width: '30px',
        position: 'absolute',
        left: '50%',
        top: '-20px',
        transform: 'rotate(-3deg) translateX(-50%)',
        borderRadius: '4px',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        right: 0,
        borderWidth: '10px 10px 10px 10px',
        borderStyle: 'solid',
        borderTopColor: '#DDC6C3',
        borderLeftColor: '#DDC6C3',
        borderBottomColor: '#5D576B',
        borderRightColor: '#5D576B',
    },
}));

export default function StickyNoteJobCost({ logs, setLogs, fetchJobCostLogs, projectData}) {
    const [content, setContent] = useState("");
    const [height, setHeight] = useState('auto');
    const userSession = JSON.parse(localStorage.getItem('user'));
    const { enqueueSnackbar } = useSnackbar();
    const [noteId, setNoteId] = useState(null);
    const [addedOn, setAddedOn] = useState(null);
    const [updatedOn, setUpdatedOn] = useState(null);
    const [userName, setUserName] = useState('');
    const [updatedName, setUpdatedName] = useState('');

    // Debug log to see what projectData contains
    console.log('StickyNoteJobCost - projectData:', projectData);
    console.log('StickyNoteJobCost - projectData.project_id:', projectData?.id);

    const fetchStickyNote = async () => {
        try {
            if (!projectData?.id) {
                console.warn("No project ID available for fetching sticky note");
                return;
            }

            const res = await axios.post(`${REST_API_END_POINT}project/get-sticky-note`, {
                project_id: projectData.id
            });
            const d = res?.data?.data ?? {};

            // set everything straight from network
            setNoteId(d?.id ?? null);
            setContent(d?.sticky_note ?? "");
            setUserName(d?.user_name ?? "N/A");
            setUpdatedName(d?.updated_by_name ?? "");     // empty if not edited yet
            setAddedOn(d?.timestamp ?? null);             // epoch seconds
            setUpdatedOn(d?.updated_on ?? null);          // epoch seconds
        } catch (e) {
            console.error("Error fetching sticky note:", e);
            // enqueueSnackbar("Failed to fetch sticky note", { variant: "error" });
        }
    };

    useEffect(() => {
        if (projectData?.id) {
            fetchStickyNote();
        } else {
            // Clear sticky note data when no project is selected
            setNoteId(null);
            setContent("");
            setUserName("");
            setUpdatedName("");
            setAddedOn(null);
            setUpdatedOn(null);
        }
    }, [projectData?.id]);

    const handleChange = (event) => {
        const value = event.target.value;
        setContent(value);

        const lineCount = value.split('\n').length;
        const minHeight = 250;
        const dynamicHeight = minHeight + (lineCount > 5 ? (lineCount - 5) * 20 : 0);
        setHeight(`${dynamicHeight}px`);
    };

    const handleSaveSticky = async () => {
        if (content === undefined) return;
        
        if (!projectData?.id) {
            enqueueSnackbar("No project selected. Please select a project first.", { variant: "warning" });
            return;
        }

        try {
            document.activeElement?.blur();

            const isEdit = !!noteId;
            const endpoint = isEdit ? "edit-sticky-note" : "add-sticky-note";
            const payload = isEdit
                ? { id: noteId, sticky_note: content.trim() || null, updated_by: userSession?.user_id, project_id: projectData.id }
                : { sticky_note: content.trim() || null, added_by: userSession?.user_id, project_id: projectData.id };

            console.log('Saving sticky note with payload:', payload); // Debug log

            const res = await axios.post(`${REST_API_END_POINT}project/${endpoint}`, payload);

            // handle both shapes: {id: 2,...} or {data:{id:2,...}}
            const savedId = res?.data?.id ?? res?.data?.data?.id ?? noteId;
            setNoteId(savedId);

            enqueueSnackbar("Sticky Note saved successfully!", { variant: "success" });
            await fetchJobCostLogs();
            // pull fresh network data so added/updated names & times show correctly
            await fetchStickyNote();
        } catch (e) {
            console.error("Error saving sticky note:", e);
            enqueueSnackbar("Failed to save Sticky Note", { variant: "error" });
        }
    };

    return (
        <StickyNote sx={{ minHeight: '244px', position: 'relative' }}>
            <Tooltip title="Save Sticky Note" placement="top" arrow>
                <IconButton
                    onClick={handleSaveSticky}
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 24,
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#388E3C',
                        },
                        width: 40,
                        height: 40,
                        zIndex: 999,
                    }}
                >
                    <Save fontSize="small" />
                </IconButton>
            </Tooltip>

            <TextField
                fullWidth
                multiline
                minRows={10}
                variant="standard"
                value={content}
                onChange={handleChange}
                placeholder="Write something....."
                InputProps={{
                    disableUnderline: true,
                    style: {
                        fontFamily: `'Loved by the King', cursive`,
                        fontSize: '14px',
                        color: '#fff',
                        padding: '12px',
                    },
                }}
                sx={{
                    height: '288px',
                    overflowY: 'auto',
                    '& .MuiInputBase-input::placeholder': {
                        color: '#ccc',
                    },
                }}
            />

            <Divider sx={{ pr: 3, pl: 3, mt: 1.8, borderColor: '#fff', }} />
            {updatedName ? (
                <Typography
                    variant='body2'
                    sx={{
                        fontFamily: `'Loved by the King', cursive`,
                        fontSize: '12px',
                        color: '#fff',
                        marginTop: '12px',
                        paddingLeft: '8px',
                    }}>
                    {`Last edited by: ${updatedName}`}
                </Typography>
            ) :
                (
                    <Typography
                        variant='body2'
                        sx={{
                            fontFamily: `'Loved by the King', cursive`,
                            fontSize: '12px',
                            color: '#fff',
                            marginTop: '12px',
                            paddingLeft: '8px',
                        }}>
                        {`Added by: ${userName}` || 'N/A'}
                    </Typography>
                )}
            <Typography variant='body2' sx={{ fontFamily: `'Loved by the King', cursive`, fontSize: '12px', color: '#fff', mt: '4px', pl: '8px' }}>
                {updatedOn
                    ? `Updated on: ${moment.unix(updatedOn).format('MM-DD-YYYY hh:mm:ss A')}`
                    : addedOn
                        ? `Added on: ${moment.unix(addedOn).format('MM-DD-YYYY hh:mm:ss A')}`
                        : 'N/A'}
            </Typography>
        </StickyNote >
    );
}
